import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { equipeIncoerente, pedirRenovacao } from '@/lib/copiaLocal';
import { ouvirLeads } from '@/services/leadsService';
import { ouvirEquipe } from '@/services/usuariosService';
import { ouvirPendentes } from '@/services/agendamentosService';
import { leadsComPendencia } from '@/agenda/pendencia';
import { ouvirClinica } from '@/services/clinicasService';
import { aplicarFiltros, filtrosIniciais } from '@/leads/filtros';
import { CADENCIA_PADRAO } from '@/leads/cadencia';
import AvisoCarga, { AvisoDesatualizado } from '@/components/AvisoCarga';

const LeadsContext = createContext(null);

/**
 * Uma única assinatura dos leads da clínica, compartilhada pelo kanban e pela
 * lista — inclusive o estado dos filtros, para que alternar entre as duas
 * visões preserve o recorte que o SDR montou.
 */
export function LeadsProvider() {
  const { clinicaId, usuario, perfil } = useAuth();
  const [leads, setLeads] = useState([]);
  const [equipe, setEquipe] = useState([]);
  const [clinica, setClinica] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  // VL: o padrão ao abrir é o MÊS ATUAL (não "tudo") — depois da V7 são ~7,5 mil leads.
  const [filtros, setFiltros] = useState(() => filtrosIniciais());
  const [leadAberto, setLeadAberto] = useState(null);

  /**
   * ★★★ WU/U3 — O SINAL DA JÉSSICA: a equipe chegou e o PRÓPRIO usuário não está nela.
   *
   * ⚠⚠ Isso **não tem explicação legítima**: quem está logado sempre está na própria equipe
   * (`usuarios` com `ativo: true`). ★★ Quando acontece, a lista veio de um retrato velho da
   * cópia local — e foi exatamente o que produziu *"Jessica Paterno (fora da função)"* por cinco
   * dias, em 29/set–03/out.
   *
   * ★ `pedirRenovacao` só MARCA (a limpeza exige a próxima carga) e respeita a trava de
   * **uma renovação por hora**; na segunda vez ele registra o erro e para de oferecer.
   */
  const receberEquipe = (lista) => {
    setEquipe(lista);
    if (equipeIncoerente(lista, usuario?.uid)) pedirRenovacao(usuario.uid);
  };

  useEffect(() => {
    // Superadmin não pertence a clínica nenhuma: não há o que assinar, e
    // deixar `carregando` ligado prenderia a tela num spinner eterno.
    if (!clinicaId) {
      setLeads([]);
      setCarregando(false);
      return undefined;
    }
    setCarregando(true);
    const cancelar = ouvirLeads(
      clinicaId,
      (lista) => {
        setLeads(lista);
        setErro(null);
        setCarregando(false);
      },
      (e) => {
        console.error('[OdontoSDR] Falha ao ouvir leads:', e);
        setErro('Não foi possível carregar os leads.');
        setCarregando(false);
      },
    );
    return cancelar;
  }, [clinicaId]);

  /**
   * ★★★ WT lote 1 — OS TRÊS LISTENERS QUE SÓ LOGAVAM.
   *
   * ⚠⚠ Equipe, agendamentos pendentes e configuração da clínica falhavam com um
   * `console.error` e nada mais (`docs/wt-medicao.md` §1.2). ★ O da EQUIPE é o outro lado
   * do caso da Jéssica: com a lista incompleta, o **filtro SDR do Funil** perde nomes — e a
   * tela não dizia que perdeu.
   *
   * ★ Agora cada um marca o seu assunto e a tela avisa.
   */
  const [falhas, setFalhas] = useState({});
  const marcarFalha = (assunto) => () => setFalhas((f) => (f[assunto] ? f : { ...f, [assunto]: true }));

  /**
   * ★★★ WU-2/P3 — "o servidor não confirmou este dado".
   *
   * ⚠ Diferente de `falhas`, este estado anda nos DOIS sentidos: ★ `ouvirVigiado` chama
   * `aoDesatualizar(false)` quando o servidor responde, então a faixa **sai sozinha** quando a
   * conexão volta. ⚠⚠ Uma faixa que só acende nunca mais apaga, e aí a equipe aprende a
   * ignorá-la.
   */
  const [desatualizados, setDesatualizados] = useState({});
  /**
   * ★★ WU-2b — estável (`useCallback`) porque vai no contexto: ★ as telas sob o provider
   * (Próximos contatos, com a meta do dia) avisam por aqui em vez de desenhar a própria
   * faixa — e uma função nova a cada render dispararia o efeito delas em laço.
   */
  const definirDesatualizado = useCallback((assunto, velho) => setDesatualizados((d) => {
    const alvo = Boolean(velho);
    if (Boolean(d[assunto]) === alvo) return d;
    return { ...d, [assunto]: alvo };
  }), []);
  const marcarDesatualizado = (assunto) => (velho) => definirDesatualizado(assunto, velho);

  useEffect(() => {
    if (!clinicaId) return undefined;
    return ouvirEquipe(
      clinicaId,
      receberEquipe,
      marcarFalha('a equipe'),
      marcarDesatualizado('a equipe'),
    );
  }, [clinicaId]);

  /**
   * ★★ WO lote 3a — os agendamentos VENCIDOS E SEM BAIXA, para o selo do card.
   * ★ Consulta estreita (757 docs, não 2.658) e índice que já existe.
   */
  const [pendentes, setPendentes] = useState([]);
  useEffect(() => {
    if (!clinicaId) return undefined;
    return ouvirPendentes(clinicaId, setPendentes, marcarFalha('os agendamentos pendentes'));
  }, [clinicaId]);

  useEffect(() => {
    if (!clinicaId) return undefined;
    return ouvirClinica(
      clinicaId,
      setClinica,
      marcarFalha('a configuração da clínica'),
      marcarDesatualizado('a configuração da clínica'),
    );
  }, [clinicaId]);

  const valor = useMemo(() => {
    const filtrados = aplicarFiltros(leads, filtros);
    return {
      // ★ WT: os assuntos que não carregaram — as telas do funil avisam em vez de calar.
      falhas: Object.keys(falhas),
      // ★★★ WU-2/P3: os assuntos que o servidor não confirmou (cache sem confirmação).
      desatualizados: Object.keys(desatualizados).filter((a) => desatualizados[a]),
      // ★★ WU-2b: a tela avisa "este assunto meu não foi confirmado" — a faixa é do provider.
      definirDesatualizado,
      leads,
      leadsFiltrados: filtrados,
      equipe,
      clinica,
      cadencia: clinica?.config?.cadenciaFollowUp ?? CADENCIA_PADRAO,
      carregando,
      erro,
      filtros,
      setFiltros,
      // "Limpar filtros" volta ao PADRÃO (mês atual), não a "tudo".
      limparFiltros: () => setFiltros(filtrosIniciais()),
      filtrosIniciais: filtrosIniciais(),
      leadAberto: leadAberto ? (leads.find((l) => l.id === leadAberto) ?? null) : null,
      abrirLead: setLeadAberto,
      fecharLead: () => setLeadAberto(null),
      // Quem assina as escritas na timeline.
      autor: { id: usuario?.uid, nome: perfil?.nome ?? usuario?.email ?? 'Usuário' },
      clinicaId,
      /**
       * ★★ Os leads com agendamento pendente — ⚠ só os agendados PELA EQUIPE.
       *
       * Medido: sem o filtro seriam **623 de 1.345** cards acesos (46% da tela), e **626**
       * deles vêm da "Importação planilha 2026". ★ Selo em metade do funil não é destaque,
       * é ruído — e ninguém vai dar baixa retroativa em consulta importada.
       * ★ Para contar tudo, basta não passar `idsDaEquipe`.
       */
      leadsComPendencia: leadsComPendencia(pendentes, {
        idsDaEquipe: new Set((equipe ?? []).map((u) => u?.id).filter(Boolean)),
      }),
    };
  }, [leads, equipe, clinica, carregando, erro, filtros, leadAberto, usuario, perfil, clinicaId, pendentes, falhas, desatualizados, definirDesatualizado]);

  /**
   * ★★★ WU-2/P2 — OS AVISOS SAEM AQUI, NO PROVIDER, E NÃO EM CADA TELA.
   *
   * ⚠⚠⚠ O buraco que isto fecha, medido em `docs/wu-reincidencia-1610.md` §4.2: o provider
   * **já marcava** as falhas desde a WT lote 1, e **nenhuma** das telas consumia `falhas` —
   * `grep` nos 10 arquivos que chamam `useLeads()`: zero destruturações. ★ Então
   * `ouvirEquipe`, `ouvirClinica` e `ouvirPendentes` podiam falhar no Funil, em Próximos
   * Contatos, no Dashboard, no Placar e em Recebimentos, e a pessoa **não via nada** — só o
   * registro em `erros` sabia. ⚠ Era metade exata do conserto proposto em 03/out.
   *
   * ★★★ POR QUE NO PROVIDER: ⚠⚠ a alternativa era a mesma linha em **14** telas, e o item
   * 15 nasceria sem ela — foi exatamente assim que a metade ficou faltando. ★ Aqui a tela
   * **não pode** esquecer: quem está sob o `LeadsProvider` avisa por construção.
   *
   * ★★ `<>…</>` e não um `<div>`: ⚠ um elemento a mais quebraria a altura calculada do funil
   * (há teste de navegador para ela). ★ Sem falha, nada é renderizado e o DOM é o de hoje —
   * idêntico, byte a byte.
   *
   * ★★★ WU-2b — "pode estar desatualizado" é **UMA** faixa, não uma por assunto: ⚠ a causa
   * é sempre a mesma (a conexão), e três faixas iguais no canário viraram ruído. ★ Ela some
   * quando o último assunto é confirmado. ⚠ As vermelhas (`falhas`) seguem uma por assunto:
   * ali cada uma diz O QUE não carregou, e isso muda o que a pessoa faz.
   */
  return (
    <LeadsContext.Provider value={valor}>
      {valor.falhas.map((assunto) => <AvisoCarga key={assunto} oQue={assunto} />)}
      {valor.desatualizados.length > 0 && <AvisoDesatualizado />}
      <Outlet />
    </LeadsContext.Provider>
  );
}

export function useLeads() {
  const ctx = useContext(LeadsContext);
  if (!ctx) throw new Error('useLeads precisa estar dentro de <LeadsProvider>.');
  return ctx;
}
